import { cn } from "@/lib/utils";
import { useState, type ReactNode } from "react";

/** Aliases for a single Lil' Dragons One Step demo in Training media (optional). */
const ONE_STEP_VIDEO_ALIASES = ["lil_dragon_one_step", "lil_dragons_one_step"];

interface LilDragonsOneStepTabContentProps {
  hasTrainingVideo: (aliases: string[]) => boolean;
  handleWatchTrainingVideo: (aliases: string[]) => void;
}

const STEPS: { heading: string; body: ReactNode }[] = [
  {
    heading: "#1 One Step",
    body: (
      <>
        Step R foot at 45° into horse stance;<br />
        <br /> simultaneous L knife-hand block and R high section punch.
      </>
    ),
  },
  {
    heading: "#2 One Step",
    body: (
      <>
        Step R foot forward into back stance;<br />
        <br /> R inner hammer block, grab attacker&apos;s R hand with your L, adjust to front stance, R back-fist
        strike to temple.
      </>
    ),
  },
  {
    heading: "#3 One Step",
    body: (
      <>
        Step R foot forward into R front stance;<br />
        <br /> simultaneous L knife-hand block and R knife-hand strike to neck.
      </>
    ),
  },
  {
    heading: "#4 One Step",
    body: (
      <>
        Step L foot at 45° into horse stance;<br />
        <br /> simultaneous R knife-hand block and L mid-section punch to ribs.
      </>
    ),
  },
  {
    heading: "#5 One Step",
    body: (
      <>
        Step R foot back into a L back stance;<br />
        <br /> R front kick into 45° horse stance; simultaneous L knife-hand block and R high section punch.
      </>
    ),
  },
  {
    heading: "#6 One Step",
    body: (
      <>
        Step R foot 45° into horse stance;<br />
        <br /> L knife-hand block with R high punch, then L mid punch.
      </>
    ),
  },
  {
    heading: "#7 One Step",
    body: (
      <>
        Step R foot forward into R back stance;<br />
        <br /> R inner hammer block (grabbing attacker&apos;s hand with L hand), R back-fist to temple, R elbow to
        ribs (in horse stance).
      </>
    ),
  },
  {
    heading: "#8 One Step",
    body: (
      <>
        Step R foot forward into R front stance;<br />
        <br /> simultaneous L knife-hand block and R knife-hand strike to neck; grab attacker&apos;s shoulders and
        pull forward, L knee to solar plexus.
      </>
    ),
  },
  {
    heading: "#9 One Step",
    body: (
      <>
        Step L foot 45° into front stance;<br />
        <br /> R outward knife-hand block, grab/twist attacker&apos;s wrist, pulling into R side kick to ribs.
      </>
    ),
  },
  {
    heading: "#10 One Step",
    body: (
      <>
        Step L foot at 45° into horse stance;<br />
        <br /> R knife-hand block, L–R double mid-section punch, R round kick to solar plexus.
      </>
    ),
  },
];

export function LilDragonsOneStepTabContent({
  hasTrainingVideo,
  handleWatchTrainingVideo,
}: LilDragonsOneStepTabContentProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const showOneStepVideo = hasTrainingVideo(ONE_STEP_VIDEO_ALIASES);

  return (
    <div>
      {/* Mobile only: title → optional video → 1–10 nav → selected step */}
      <div className="flex flex-col gap-4 md:hidden">
        <h3 className="text-lg font-semibold text-taekwondo-red">One Step</h3>
        {showOneStepVideo && (
          <button
            type="button"
            className="inline-flex w-fit items-center text-sm font-medium text-taekwondo-blue hover:underline"
            onClick={() => handleWatchTrainingVideo(ONE_STEP_VIDEO_ALIASES)}
          >
            Watch Demonstration Video
          </button>
        )}
        <div
          className="grid w-full grid-cols-5 gap-2 sm:grid-cols-10 sm:gap-1"
          role="tablist"
          aria-label="One Step techniques"
        >
          {STEPS.map((_, index) => {
            const num = index + 1;
            const isSelected = activeIndex === index;
            return (
              <button
                key={num}
                type="button"
                role="tab"
                aria-selected={isSelected}
                aria-label={`One Step ${num}`}
                onClick={() => setActiveIndex(index)}
                className={cn(
                  "flex min-w-0 flex-col items-center justify-center rounded-xl border-2 bg-white px-0.5 py-2 text-sm font-semibold tabular-nums transition-colors sm:text-base",
                  isSelected
                    ? "border-taekwondo-blue shadow-md shadow-taekwondo-blue/15 ring-2 ring-taekwondo-blue/25 text-taekwondo-red"
                    : "border-gray-200 text-gray-800 hover:border-taekwondo-blue/40",
                )}
              >
                {num}
              </button>
            );
          })}
        </div>
        <div
          className="bg-white rounded-lg p-4 shadow-sm flex flex-col items-start gap-4"
          role="tabpanel"
        >
          <img
            src="/images/dragons/dragon_one.png"
            alt="Lil' Dragon one-step"
            className="h-24 w-auto flex-shrink-0"
            loading="lazy"
            decoding="async"
          />
          <div className="text-left text-sm text-gray-700">
            <h4 className="text-md font-semibold mb-1">{STEPS[activeIndex].heading}</h4>
            <p>{STEPS[activeIndex].body}</p>
          </div>
        </div>
      </div>

      {/* Desktop: direct copy of original page markup */}
      <div className="mt-10 hidden md:block">
        <h3 className="text-lg font-semibold text-taekwondo-red mb-4">One Step</h3>
        <div className="space-y-4">
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#1 One Step</h4>
              <p>
                Step R foot at 45° into horse stance;<br/><br/> simultaneous L knife-hand
                block and R high section punch.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#2 One Step</h4>
              <p>
                Step R foot forward into back stance;<br/><br/> R inner hammer block, grab
                attacker&apos;s R hand with your L, adjust to front stance, R
                back-fist strike to temple.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#3 One Step</h4>
              <p>
                Step R foot forward into R front stance;<br/><br/> simultaneous L knife-hand
                block and R knife-hand strike to neck.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#4 One Step</h4>
              <p>
                Step L foot at 45° into horse stance;<br/><br/> simultaneous R knife-hand
                block and L mid-section punch to ribs.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#5 One Step</h4>
              <p>
                Step R foot back into a L back stance;<br/><br/> R front kick into 45° horse
                stance; simultaneous L knife-hand block and R high section punch.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#6 One Step</h4>
              <p>
                Step R foot 45° into horse stance;<br/><br/> L knife-hand block with R high
                punch, then L mid punch.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#7 One Step</h4>
              <p>
                Step R foot forward into R back stance;<br/><br/> R inner hammer block
                (grabbing attacker&apos;s hand with L hand), R back-fist to temple,
                R elbow to ribs (in horse stance).
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#8 One Step</h4>
              <p>
                Step R foot forward into R front stance;<br/><br/> simultaneous L knife-hand
                block and R knife-hand strike to neck; grab attacker&apos;s
                shoulders and pull forward, L knee to solar plexus.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#9 One Step</h4>
              <p>
                Step L foot 45° into front stance;<br/><br/> R outward knife-hand block,
                grab/twist attacker&apos;s wrist, pulling into R side kick to ribs.
              </p>
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm flex flex-col md:flex-row items-start gap-4">
            <img
              src="/images/dragons/dragon_one.png"
              alt="Lil' Dragon one-step"
              className="h-24 w-auto flex-shrink-0"
            />
            <div className="text-left text-sm text-gray-700">
              <h4 className="text-md font-semibold mb-1">#10 One Step</h4>
              <p>
                Step L foot at 45° into horse stance;<br/><br/> R knife-hand block, L–R
                double mid-section punch, R round kick to solar plexus.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
